import React, {FC, ReactNode, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {ALIGN, Box, COLOR, Layout as AuroraLayout, SPACE, useDevice, View} from '@lookiero/aurora';

import {useApp} from '@app';
import {CONTENT_PROPS} from '@app/App.config';
import {Layout} from '@components/layouts/layout/Layout';
import {SubNavigation} from '@components/molecules/SubNavigation';
import {useHardwareBackPress} from '@components/hooks/useHardwareBackPress';
import {usePageLoad} from '@tracking/usePageLoad';
import {PageEventView} from '@domain/model/Event/domain/model/UiEvents';

import {SECTION_SPACE} from '../../DeleteAccount.definition';
import {style} from './DeleteAccountLayout.style';

const SHARED_TEXT = {
  HEADER: {id: 'deleteAccount.shared.header'},
};

const OUTER_VERTICAL_SPACE = {M: SPACE.SPACE_12, L: SPACE.SPACE_12};

const NO_BACK = (): boolean => false;

interface DeleteAccountLayoutProps {
  readonly pageView: PageEventView;
  readonly onBack?: () => void;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

const DeleteAccountLayout: FC<DeleteAccountLayoutProps> = ({pageView, onBack, footer, children}) => {
  const {setLoading} = useApp();
  const {formatMessage} = useIntl();
  const {screen} = useDevice();

  useHardwareBackPress(onBack ?? NO_BACK);

  useEffect(() => setLoading(false), [setLoading]);

  usePageLoad(pageView);

  return (
    <Layout
      header={<SubNavigation title={formatMessage(SHARED_TEXT.HEADER)} onBack={onBack} />}
      footer={null}
      style={{safeAreaView: !screen.S ? style.screen : undefined}}
    >
      <AuroraLayout
        inline={false}
        alignContent={ALIGN.CENTER}
        fullWidth={screen.S}
        paddingTop={OUTER_VERTICAL_SPACE}
        paddingBottom={OUTER_VERTICAL_SPACE}
        style={style.layout}
      >
        <Box
          {...CONTENT_PROPS}
          backgroundColor={COLOR.BG_BASE}
          paddingHorizontal={SPACE.SPACE_6}
          paddingTop={SECTION_SPACE}
          paddingBottom={SECTION_SPACE}
          justifyContent={footer ? ALIGN.BETWEEN : undefined}
          style={[style.content, !screen.S && style.rounded]}
        >
          {footer ? (
            <>
              <View>{children}</View>
              <View paddingTop={SPACE.SPACE_8}>{footer}</View>
            </>
          ) : (
            children
          )}
        </Box>
      </AuroraLayout>
    </Layout>
  );
};

export {DeleteAccountLayout};
